import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  hydrateFromGuestEvent,
  setEntryError,
  setEntryLoading,
  setMockEvent,
  setUrlParams,
} from '../../store/slices/urlParamsSlice';
import { extractAndDecodeUrlParams } from '../../utils/urlParamsUtils';
import { eventServices } from '../../services/eventServices';

const guestTokenStorageKey = (eventId) => `guest-upload-token:${eventId}`;

const readStoredGuestToken = (eventId) => {
  if (!eventId) return null;
  try {
    return sessionStorage.getItem(guestTokenStorageKey(eventId));
  } catch (_) {
    return null;
  }
};

const storeGuestToken = (eventId, token) => {
  if (!eventId || !token) return;
  try {
    sessionStorage.setItem(guestTokenStorageKey(eventId), token);
  } catch (_) {
    // ignore quota / private mode
  }
};

const extractEventId = (search, legacy) => {
  const sp = new URLSearchParams(search);
  const fromQuery = sp.get('eventId');
  if (fromQuery && fromQuery.trim()) return fromQuery.trim();
  if (legacy?.eventId) return legacy.eventId;
  return null;
};

const UrlParamsHandler = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const legacy = extractAndDecodeUrlParams();
    const eventId = extractEventId(window.location.search, legacy);
    const tokenFromUrl =
      new URLSearchParams(window.location.search).get('token') || legacy?.token || null;

    if (!eventId) {
      // Legacy full URL (eventCode in query) still works
      if (legacy?.eventCode) {
        if (legacy.eventId && legacy.token) {
          storeGuestToken(legacy.eventId, legacy.token);
        }
        dispatch(setUrlParams(legacy));
        return undefined;
      }
      // No event link — open demo/mock event
      dispatch(setMockEvent());
      return undefined;
    }

    dispatch(setEntryLoading());

    let cancelled = false;
    eventServices
      .getGuestEvent(eventId)
      .then(async (payload) => {
        if (cancelled) return;
        const p = payload?.data && typeof payload.data === 'object' ? payload.data : payload;

        const eventCode = p?.eventCode;
        if (!eventCode) throw new Error('Missing eventCode');

        // Prefer mainEventPhoto from getGuestEvent; if absent, fetch it explicitly
        // from /media/main-photo/:eventCode (the Firebase URL stored in MongoDB).
        let mainEventPhoto = p?.mainEventPhoto || null;
        if (!mainEventPhoto) {
          try {
            const photoData = await eventServices.getMainPhotoPublic(eventCode);
            mainEventPhoto =
              photoData?.mainEventPhoto ||
              photoData?.url ||
              (typeof photoData === 'string' ? photoData : null);
          } catch (_) {
            // Not fatal — fall back to the placeholder image
          }
        }

        if (cancelled) return;

        const resolvedEventId = p?.eventId || eventId;
        const guestToken =
          p?.token || p?.guestToken || p?.uploadToken || tokenFromUrl || readStoredGuestToken(resolvedEventId);

        if (guestToken) {
          storeGuestToken(resolvedEventId, guestToken);
        }

        dispatch(hydrateFromGuestEvent({
          eventId: resolvedEventId,
          eventCode,
          templateId: p?.templateId || p?.theme,
          mainEventPhoto,
          token: guestToken || null,
        }));
        // Keep URL short (ignore any extra legacy params)
        window.history.replaceState({}, '', `/?eventId=${encodeURIComponent(eventId)}`);
      })
      .catch((err) => {
        if (cancelled) return;
        const s = err?.response?.status;
        const msg =
          s === 404
            ? 'Event not found or expired.'
            : s === 403
              ? 'This event is closed.'
              : 'Could not load this event. Please try again.';
        dispatch(setEntryError(msg));
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return null;
};

export default UrlParamsHandler; 